import { User } from '@/types';
import { apiClient } from './apiClient';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  user?: User;
  errorCode?: string;
}

/**
 * Authentication service for the TV Streaming Admin API
 * Uses HTTP-only cookies with JWT tokens for secure authentication
 */
export const authService = {
  /**
   * Login user with username and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClient.login(credentials);
      
      if (response.success) {
        return {
          success: true,
          message: response.message,
          user: response.user,
        };
      } else {
        return {
          success: false,
          message: response.message || 'Login failed',
          errorCode: response.errorCode,
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Login failed',
      };
    }
  },

  /**
   * Logout current user
   */
  async logout(): Promise<boolean> {
    try {
      await apiClient.logout();
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      // Return true even if the request fails (user should be logged out locally)
      return true;
    }
  },

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<LoginResponse> {
    try {
      const response = await apiClient.refreshToken();
      
      if (response.success) {
        return {
          success: true,
          message: response.message,
          user: response.user,
        };
      } else {
        return {
          success: false,
          message: response.message || 'Token refresh failed',
          errorCode: response.errorCode,
        };
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Token refresh failed',
      };
    }
  },

  /**
   * Check if user is currently authenticated by making a test API call
   */
  async checkAuthStatus(): Promise<LoginResponse> {
    try {
      // Try to refresh token to validate current session
      return await this.refreshToken();
    } catch {
      return {
        success: false,
        message: 'Not authenticated',
      };
    }
  },
};